import { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from "react";
import { OutfitFolder, Outfit, OutfitItem, ClothingItem, OutfitTag } from "../types";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAuth } from "./AuthContext";
import { useAdminViewAs } from "./AdminViewAsContext";
import { mapDbItemToClothingItem } from "@/utils/clothingMappers";
import { withTimeout, DEFAULT_QUERY_TIMEOUT_MS } from "@/utils/withTimeout";

export interface OutfitWithVariations extends Outfit {
  variation_count: number;
}

interface OutfitContextType {
  folders: OutfitFolder[];
  outfits: Outfit[];
  primaryOutfits: OutfitWithVariations[];
  isLoading: boolean;
  createFolder: (folderData: { name: string; description?: string }) => Promise<OutfitFolder | null>;
  updateFolder: (id: string, updates: Partial<OutfitFolder>) => Promise<void>;
  deleteFolder: (id: string) => Promise<void>;
  createOutfit: (outfitData: { name: string; description?: string; notes?: string; folder_id?: string; spotlight?: boolean; items: string[]; user_id?: string }) => Promise<Outfit | null>;
  updateOutfit: (id: string, updates: Partial<Outfit>) => Promise<void>;
  deleteOutfit: (id: string) => Promise<void>;
  addItemToOutfit: (outfitId: string, clothingItemId: string, position?: number) => Promise<void>;
  removeItemFromOutfit: (outfitItemId: string) => Promise<void>;
  reorderOutfitItems: (outfitId: string, itemIds: string[]) => Promise<void>;
  getOutfitsByFolder: (folderId: string) => Outfit[];
  getFolderById: (id: string) => OutfitFolder | undefined;
  getOutfitById: (id: string) => Outfit | undefined;
  getOutfitItems: (outfitId: string) => Promise<OutfitItem[]>;
  isSharedCloset: boolean;
  addProductToOutfit: (outfitId: string, productId: string, position?: number) => Promise<void>;
  removeProductFromOutfit: (outfitProductItemId: string) => Promise<void>;
  getOutfitTags: (outfitId: string) => Promise<OutfitTag[]>;
  addOutfitTag: (outfitId: string, tagName: string) => Promise<void>;
  removeOutfitTag: (tagId: string) => Promise<void>;
  getRelatedItems: (outfitId: string) => Promise<ClothingItem[]>;
  addRelatedItem: (outfitId: string, clothingItemId: string) => Promise<void>;
  removeRelatedItem: (outfitId: string, clothingItemId: string) => Promise<void>;
  addOutfitInspiration: (inspiredOutfitId: string, sourceOutfitId: string) => Promise<void>;
  getInspiredOutfits: (sourceOutfitId: string) => Promise<Outfit[]>;
  duplicateOutfitAsVariation: (outfitId: string, newName?: string) => Promise<Outfit | null>;
  getOutfitVariations: (outfitId: string) => Promise<Outfit[]>;
  refreshOutfits: () => Promise<void>;
}

// ---- Pure row -> OutfitItem mappers ----------------------------------------
// Shared by getOutfitItems (per-outfit live query) and the get_outfit_bundles
// prefetch, so both paths produce byte-identical OutfitItem shapes.
const mapClothingRow = (item: any): OutfitItem => {
  const clothingItem = item.clothing_items;
  const mappedClothingItem = clothingItem ? {
    id: clothingItem.id,
    name: clothingItem.name || "",
    brand: clothingItem.brand || "",
    color: clothingItem.color || "",
    category: clothingItem.category || "",
    subcategories: clothingItem.subcategories || [],
    status: (clothingItem.status as any) || "PURCHASED",
    storeUrl: clothingItem.store_url || "",
    images: clothingItem.image_url ? [clothingItem.image_url] : [],
    user_id: clothingItem.user_id,
    description: clothingItem.description || "",
    favorite: clothingItem.favorite || false,
    lifestyle_tags: clothingItem.lifestyle_tags || []
  } : undefined;

  return {
    id: item.id,
    outfit_id: item.outfit_id,
    clothing_item_id: item.clothing_item_id,
    position: item.position,
    clothingItem: mappedClothingItem,
    isProductRecommendation: false,
    stylistNotes: item.stylist_notes
  };
};

const mapProductRow = (item: any, uid: string): OutfitItem => {
  const productItem = item.product_recommendations;
  return {
    id: item.id,
    outfit_id: item.outfit_id,
    clothing_item_id: item.product_id, // Use product_id as clothing_item_id
    position: item.position,
    clothingItem: productItem ? {
      id: productItem.id,
      name: productItem.product_name || "",
      brand: productItem.brand || "",
      color: "",
      category: productItem.category || "",
      subcategories: [],
      status: "WISHLIST" as any,
      storeUrl: productItem.product_link || "",
      images: productItem.image_url ? [productItem.image_url] : [],
      user_id: uid || "",
      description: productItem.stylist_notes || "",
      favorite: false,
      lifestyle_tags: []
    } : undefined,
    isProductRecommendation: true,
    stylistNotes: item.stylist_notes
  };
};

// Combine clothing + product items, dedupe by clothing_item_id (preferring the
// owned closet item over a product recommendation), then sort by position.
const dedupeAndSortItems = (allItems: OutfitItem[]): OutfitItem[] => {
  const itemMap = new Map<any, OutfitItem>();
  allItems.forEach(item => {
    const key = item.clothing_item_id;
    const existing = itemMap.get(key);
    if (!existing) {
      itemMap.set(key, item);
    } else if (item.isProductRecommendation && !existing.isProductRecommendation) {
      return;
    } else if (!item.isProductRecommendation && existing.isProductRecommendation) {
      itemMap.set(key, item);
    } else if (item.position < existing.position) {
      itemMap.set(key, item);
    }
  });
  return Array.from(itemMap.values()).sort((a, b) => a.position - b.position);
};

const OutfitContext = createContext<OutfitContextType | undefined>(undefined);

export const OutfitProvider = ({ children }: { children: ReactNode }) => {
  const [folders, setFolders] = useState<OutfitFolder[]>([]);
  const [outfits, setOutfits] = useState<Outfit[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  // Prefetched items/tags per outfit id (from get_outfit_bundles). getOutfitItems /
  // getOutfitTags read these on a fast path; a missing entry falls back to a live query.
  const [outfitItemsById, setOutfitItemsById] = useState<Record<string, OutfitItem[]>>({});
  const [outfitTagsById, setOutfitTagsById] = useState<Record<string, OutfitTag[]>>({});
  const { user, sharedAccess } = useAuth();
  const { viewAsUser, isAdminViewMode } = useAdminViewAs();
  
  // Priority order: admin view-as > shared access > logged-in user
  const userId = viewAsUser?.id || sharedAccess?.userId || user?.id;
  const isSharedCloset = !!sharedAccess && !isAdminViewMode;
  // Always filter to customer_visible outfits (admin view-as should match customer view)
  const isPrivilegedUser = false;

  // One-shot prefetch of items + tags for ALL of the user's visible outfits, in a
  // single RPC round-trip. Returns the maps; callers decide how to apply them so a
  // failure here never blocks the outfit list (cards just fall back to per-card fetch).
  const fetchOutfitBundles = useCallback(async (uid: string) => {
    // `get_outfit_bundles` is a new RPC not yet in the generated Supabase types.
    const { data, error } = (await withTimeout(
      (supabase as any).rpc('get_outfit_bundles', { p_user_id: uid }),
      DEFAULT_QUERY_TIMEOUT_MS,
      'load outfit bundles'
    )) as any;
    if (error) throw error;

    const itemsMap: Record<string, OutfitItem[]> = {};
    const tagsMap: Record<string, OutfitTag[]> = {};
    (data || []).forEach((bundle: any) => {
      const mappedClothing = (bundle.items || []).map(mapClothingRow);
      const mappedProducts = (bundle.product_items || []).map((it: any) => mapProductRow(it, uid || ""));
      itemsMap[bundle.outfit_id] = dedupeAndSortItems([...mappedClothing, ...mappedProducts]);
      tagsMap[bundle.outfit_id] = (bundle.tags || []) as OutfitTag[];
    });
    return { itemsMap, tagsMap };
  }, []);

  // Drop a stale prefetched entry (or all of them) so the next getOutfitItems call
  // re-fetches fresh — used after item/product mutations edit an outfit's contents.
  const invalidateOutfitItems = useCallback((outfitId?: string) => {
    if (outfitId) {
      setOutfitItemsById(prev => {
        if (!(outfitId in prev)) return prev;
        const next = { ...prev };
        delete next[outfitId];
        return next;
      });
    } else {
      setOutfitItemsById({});
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!userId) {
        setFolders([]);
        setOutfits([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        // Fetch folders
        const { data: folderData, error: folderError } = await withTimeout(
          supabase
            .from('outfit_folders')
            .select('*')
            .eq('user_id', userId),
          DEFAULT_QUERY_TIMEOUT_MS,
          'load folders'
        );

        if (folderError) throw folderError;

        // Fetch outfits (exclude archived ones for Style Magazine)
        let outfitQuery = supabase
          .from('outfits')
          .select('*')
          .eq('user_id', userId)
          .eq('archived', false);

        // Customers only see outfits marked as visible
        if (!isPrivilegedUser) {
          outfitQuery = outfitQuery.eq('customer_visible', true);
        }

        // Load the outfit list AND every outfit's items/tags in parallel. The bundle
        // prefetch collapses the per-card N+1 into one RPC; if it fails, the list still
        // renders and cards fall back to per-card fetches.
        const [outfitResult, bundleResult] = await Promise.all([
          withTimeout(outfitQuery, DEFAULT_QUERY_TIMEOUT_MS, 'load outfits'),
          fetchOutfitBundles(userId).catch((err) => {
            console.error('[OutfitContext] get_outfit_bundles prefetch failed; cards will fall back to per-card fetch', err);
            return null;
          }),
        ]);

        const { data: outfitData, error: outfitError } = outfitResult;
        if (outfitError) throw outfitError;

        setFolders(folderData);
        setOutfits(outfitData);
        setOutfitItemsById(bundleResult?.itemsMap ?? {});
        setOutfitTagsById(bundleResult?.tagsMap ?? {});
      } catch (error: any) {
        console.error('Error fetching outfit data:', error);
        toast.error('Failed to load outfits');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [userId, isPrivilegedUser, fetchOutfitBundles]);

  const createFolder = useCallback(async (folderData: { name: string; description?: string }): Promise<OutfitFolder | null> => {
    if (!userId) {
      toast.error('Please log in to create folders');
      return null;
    }
    
    if (isSharedCloset) {
      toast.error('Cannot create folders in a shared closet');
      return null;
    }

    try {
      const newFolder = {
        ...folderData,
        user_id: userId
      };

      const { data, error } = await supabase
        .from('outfit_folders')
        .insert(newFolder)
        .select()
        .single();

      if (error) throw error;

      setFolders(prev => [...prev, data]);
      toast.success('Folder created successfully');
      return data;
    } catch (error: any) {
      console.error('Error creating folder:', error);
      toast.error('Failed to create folder');
      return null;
    }
  }, [userId, isSharedCloset]);

  const updateFolder = useCallback(async (id: string, updates: Partial<OutfitFolder>): Promise<void> => {
    if (!userId) {
      toast.error('Please log in to update folders');
      return;
    }
    
    if (isSharedCloset) {
      toast.error('Cannot modify folders in a shared closet');
      return;
    }

    try {
      const { error } = await supabase
        .from('outfit_folders')
        .update(updates)
        .eq('id', id)
        .eq('user_id', userId);

      if (error) throw error;

      setFolders(prev => prev.map(folder => 
        folder.id === id ? { ...folder, ...updates } : folder
      ));
      
      toast.success('Folder updated');
    } catch (error: any) {
      console.error('Error updating folder:', error);
      toast.error('Failed to update folder');
    }
  }, [userId, isSharedCloset]);

  const deleteFolder = useCallback(async (id: string): Promise<void> => {
    if (!userId) {
      toast.error('Please log in to delete folders');
      return;
    }
    
    if (isSharedCloset) {
      toast.error('Cannot delete folders in a shared closet');
      return;
    }

    try {
      const { error } = await supabase
        .from('outfit_folders')
        .delete()
        .eq('id', id)
        .eq('user_id', userId);

      if (error) throw error;

      setFolders(prev => prev.filter(folder => folder.id !== id));
      // Also filter out outfits that belonged to this folder
      setOutfits(prev => prev.filter(outfit => outfit.folder_id !== id));
      
      toast.success('Folder deleted');
    } catch (error: any) {
      console.error('Error deleting folder:', error);
      toast.error('Failed to delete folder');
    }
  }, [userId, isSharedCloset]);

  const createOutfit = useCallback(async (outfitData: { 
    name: string; 
    description?: string; 
    folder_id?: string;
    spotlight?: boolean;
    items: string[] 
  }): Promise<Outfit | null> => {
    if (!userId) {
      toast.error('Please log in to create outfits');
      return null;
    }
    
    if (isSharedCloset) {
      toast.error('Cannot create outfits in a shared closet');
      return null;
    }

    try {
      // Fetch user's current season
      const { data: profileData } = await supabase
        .from('profiles')
        .select('current_season')
        .eq('id', userId)
        .single();

      // First create the outfit
      const newOutfit = {
        name: outfitData.name,
        description: outfitData.description,
        folder_id: outfitData.folder_id,
        spotlight: outfitData.spotlight || false,
        user_id: userId,
        season: profileData?.current_season ?? 1
      };

      const { data: outfitResult, error: outfitError } = await supabase
        .from('outfits')
        .insert(newOutfit)
        .select()
        .single();

      if (outfitError) throw outfitError;

      // Then add the clothing items and product recommendations to the outfit
      const clothingItemsToAdd = [];
      const productItemsToAdd = [];

      // Separate clothing items from product recommendations
      for (const itemId of outfitData.items) {
        // Check if this is a product recommendation or a clothing item
        const { data: productCheck } = await supabase
          .from('product_recommendations')
          .select('id')
          .eq('id', itemId)
          .eq('archived', false)
          .maybeSingle();


        if (productCheck) {
          // This is a product recommendation
          productItemsToAdd.push({
            outfit_id: outfitResult.id,
            product_id: itemId,
            position: productItemsToAdd.length
          });
        } else {
          // This is a clothing item
          clothingItemsToAdd.push({
            outfit_id: outfitResult.id,
            clothing_item_id: itemId,
            position: clothingItemsToAdd.length
          });
        }
      }

      // Insert clothing items
      if (clothingItemsToAdd.length > 0) {
        const { error: clothingItemsError } = await supabase
          .from('outfit_items')
          .insert(clothingItemsToAdd);

        if (clothingItemsError) throw clothingItemsError;
      }

      // Insert product items
      if (productItemsToAdd.length > 0) {
        const { error: productItemsError } = await supabase
          .from('outfit_product_items')
          .insert(productItemsToAdd);

        if (productItemsError) throw productItemsError;
      }

      const newOutfitWithItems = {
        ...outfitResult,
        items: clothingItemsToAdd.map((item) => ({
          id: '', // We don't know the generated IDs yet
          outfit_id: outfitResult.id,
          clothing_item_id: item.clothing_item_id,
          position: item.position
        }))
      };

      setOutfits(prev => [...prev, newOutfitWithItems]);
      toast.success('Outfit created successfully');

      // Generate outfit writing ideas in the background for this specific outfit
      supabase.functions.invoke('outfit-writing-v2', {
        body: { userId, outfitId: outfitResult.id }
      }).catch((err) => {
        console.error('Failed to generate outfit writing ideas:', err);
      });

      return newOutfitWithItems;
    } catch (error: any) {
      console.error('Error creating outfit:', error);
      toast.error('Failed to create outfit');
      return null;
    }
  }, [userId, isSharedCloset]);

  const updateOutfit = useCallback(async (id: string, updates: Partial<Outfit>): Promise<void> => {
    if (!userId) {
      toast.error('Please log in to update outfits');
      return;
    }
    
    if (isSharedCloset) {
      toast.error('Cannot update outfits in a shared closet');
      return;
    }

    try {
      const { error } = await supabase
        .from('outfits')
        .update(updates as any)
        .eq('id', id)
        .eq('user_id', userId);

      if (error) throw error;

      setOutfits(prev => prev.map(outfit => 
        outfit.id === id ? { ...outfit, ...updates } : outfit
      ));
      
      toast.success('Outfit updated');
    } catch (error: any) {
      console.error('Error updating outfit:', error);
      toast.error('Failed to update outfit');
    }
  }, [userId, isSharedCloset]);

  const deleteOutfit = useCallback(async (id: string): Promise<void> => {
    if (!userId) {
      toast.error('Please log in to delete outfits');
      return;
    }
    
    if (isSharedCloset) {
      toast.error('Cannot delete outfits in a shared closet');
      return;
    }

    try {
      const { error } = await supabase
        .from('outfits')
        .delete()
        .eq('id', id)
        .eq('user_id', userId);

      if (error) throw error;

      setOutfits(prev => prev.filter(outfit => outfit.id !== id));
      toast.success('Outfit deleted');
    } catch (error: any) {
      console.error('Error deleting outfit:', error);
      toast.error('Failed to delete outfit');
    }
  }, [userId, isSharedCloset]);

  const addItemToOutfit = useCallback(async (outfitId: string, clothingItemId: string, position?: number): Promise<void> => {
    if (!userId) {
      toast.error('Please log in to update outfits');
      return;
    }
    
    if (isSharedCloset) {
      toast.error('Cannot modify outfits in a shared closet');
      return;
    }

    try {
      // Get max position if not provided
      let itemPosition = position;
      if (itemPosition === undefined) {
        const { data, error } = await supabase
          .from('outfit_items')
          .select('position')
          .eq('outfit_id', outfitId)
          .order('position', { ascending: false })
          .limit(1);

        if (error) throw error;
        itemPosition = data.length > 0 ? data[0].position + 1 : 0;
      }

      const newItem = {
        outfit_id: outfitId,
        clothing_item_id: clothingItemId,
        position: itemPosition
      };

      const { data, error } = await supabase
        .from('outfit_items')
        .insert(newItem)
        .select()
        .single();

      if (error) throw error;

      // Update local state
      setOutfits(prev => prev.map(outfit => {
        if (outfit.id === outfitId) {
          const updatedItems = outfit.items ? [...outfit.items, data] : [data];
          return { ...outfit, items: updatedItems };
        }
        return outfit;
      }));
      
      invalidateOutfitItems(outfitId);
      toast.success('Item added to outfit');
    } catch (error: any) {
      console.error('Error adding item to outfit:', error);
      toast.error('Failed to add item to outfit');
    }
  }, [userId, isSharedCloset, invalidateOutfitItems]);

  const removeItemFromOutfit = useCallback(async (outfitItemId: string): Promise<void> => {
    if (!userId) {
      toast.error('Please log in to update outfits');
      return;
    }
    
    if (isSharedCloset) {
      toast.error('Cannot modify outfits in a shared closet');
      return;
    }

    try {
      const { error } = await supabase
        .from('outfit_items')
        .delete()
        .eq('id', outfitItemId);

      if (error) throw error;

      // Update local state
      setOutfits(prev => prev.map(outfit => {
        if (outfit.items) {
          const updatedItems = outfit.items.filter(item => item.id !== outfitItemId);
          return { ...outfit, items: updatedItems };
        }
        return outfit;
      }));
      
      invalidateOutfitItems();
      toast.success('Item removed from outfit');
    } catch (error: any) {
      console.error('Error removing item from outfit:', error);
      toast.error('Failed to remove item from outfit');
    }
  }, [userId, isSharedCloset, invalidateOutfitItems]);

  const reorderOutfitItems = useCallback(async (outfitId: string, itemIds: string[]): Promise<void> => {
    if (!userId) {
      toast.error('Please log in to update outfits');
      return;
    }
    
    if (isSharedCloset) {
      toast.error('Cannot modify outfits in a shared closet');
      return;
    }

    try {
      // Update each item's position
      for (let i = 0; i < itemIds.length; i++) {
        const { error } = await supabase
          .from('outfit_items')
          .update({ position: i })
          .eq('id', itemIds[i]);

        if (error) throw error;
      }

      // Update local state
      setOutfits(prev => prev.map(outfit => {
        if (outfit.id === outfitId && outfit.items) {
          const reorderedItems = [...outfit.items].sort((a, b) => {
            const aIndex = itemIds.indexOf(a.id);
            const bIndex = itemIds.indexOf(b.id);
            return aIndex - bIndex;
          });
          return { ...outfit, items: reorderedItems };
        }
        return outfit;
      }));
      
      invalidateOutfitItems(outfitId);
      toast.success('Outfit items reordered');
    } catch (error: any) {
      console.error('Error reordering outfit items:', error);
      toast.error('Failed to reorder outfit items');
    }
  }, [userId, isSharedCloset, invalidateOutfitItems]);

  const getOutfitsByFolder = useCallback((folderId: string): Outfit[] => {
    return outfits.filter(outfit => outfit.folder_id === folderId);
  }, [outfits]);

  const getFolderById = useCallback((id: string): OutfitFolder | undefined => {
    return folders.find(folder => folder.id === id);
  }, [folders]);

  const getOutfitById = useCallback((id: string): Outfit | undefined => {
    return outfits.find(outfit => outfit.id === id);
  }, [outfits]);

  const getOutfitItems = useCallback(async (outfitId: string): Promise<OutfitItem[]> => {
    // Fast path: use the one-shot prefetch when this outfit is already loaded.
    const preloaded = outfitItemsById[outfitId];
    if (preloaded !== undefined) return preloaded;

    try {

      // Fetch both clothing items and product items in parallel for faster loading
      const [clothingResult, productResult] = await withTimeout(
        Promise.all([
          supabase
            .from('outfit_items')
            .select(`
              id,
              outfit_id,
              clothing_item_id,
              position,
              stylist_notes,
              clothing_items:clothing_item_id (*)
            `)
            .eq('outfit_id', outfitId)
            .order('position'),

          supabase
            .from('outfit_product_items')
            .select(`
              id,
              outfit_id,
              product_id,
              position,
              stylist_notes,
              product_recommendations:product_id (*)
            `)
            .eq('outfit_id', outfitId)
            .order('position')
        ]),
        DEFAULT_QUERY_TIMEOUT_MS,
        'load outfit items'
      );

      const { data: clothingItems, error: clothingError } = clothingResult;
      const { data: productItems, error: productError } = productResult;

      if (clothingError) {
        console.error("Error fetching clothing items:", clothingError);
        throw clothingError;
      }

      if (productError) {
        console.error("Error fetching product items:", productError);
        throw productError;
      }


      const mappedClothingItems = (clothingItems || []).map(mapClothingRow);
      const mappedProductItems = (productItems || []).map((item: any) => mapProductRow(item, userId || ""));
      return dedupeAndSortItems([...mappedClothingItems, ...mappedProductItems]);
    } catch (error) {
      console.error('Error fetching outfit items:', error);
      throw error;
    }
  }, [userId, outfitItemsById]);

  const addProductToOutfit = useCallback(async (outfitId: string, productId: string, position?: number): Promise<void> => {
    if (!userId) {
      toast.error('Please log in to update outfits');
      return;
    }
    
    if (isSharedCloset) {
      toast.error('Cannot modify outfits in a shared closet');
      return;
    }

    try {
      // Get max position if not provided
      let itemPosition = position;
      if (itemPosition === undefined) {
        const { data, error } = await supabase
          .from('outfit_product_items')
          .select('position')
          .eq('outfit_id', outfitId)
          .order('position', { ascending: false })
          .limit(1);

        if (error) throw error;
        itemPosition = data.length > 0 ? data[0].position + 1 : 0;
      }

      const newItem = {
        outfit_id: outfitId,
        product_id: productId,
        position: itemPosition
      };

      const { data, error } = await supabase
        .from('outfit_product_items')
        .insert(newItem)
        .select()
        .single();

      if (error) throw error;
      
      invalidateOutfitItems(outfitId);
      toast.success('Product added to outfit');
    } catch (error: any) {
      console.error('Error adding product to outfit:', error);
      toast.error('Failed to add product to outfit');
    }
  }, [userId, isSharedCloset, invalidateOutfitItems]);

  const removeProductFromOutfit = useCallback(async (outfitProductItemId: string): Promise<void> => {
    if (!userId) {
      toast.error('Please log in to update outfits');
      return;
    }
    
    if (isSharedCloset) {
      toast.error('Cannot modify outfits in a shared closet');
      return;
    }

    try {
      const { error } = await supabase
        .from('outfit_product_items')
        .delete()
        .eq('id', outfitProductItemId);

      if (error) throw error;
      
      invalidateOutfitItems();
      toast.success('Product removed from outfit');
    } catch (error: any) {
      console.error('Error removing product from outfit:', error);
      toast.error('Failed to remove product from outfit');
    }
  }, [userId, isSharedCloset, invalidateOutfitItems]);

  const getOutfitTags = useCallback(async (outfitId: string): Promise<OutfitTag[]> => {
    // Fast path: use the one-shot prefetch when this outfit is already loaded.
    const preloaded = outfitTagsById[outfitId];
    if (preloaded !== undefined) return preloaded;

    try {
      const { data, error } = await supabase
        .from('outfit_tags')
        .select('*')
        .eq('outfit_id', outfitId)
        .order('created_at');

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching outfit tags:', error);
      return [];
    }
  }, [outfitTagsById]);

  const addOutfitTag = useCallback(async (outfitId: string, tagName: string): Promise<void> => {
    if (!userId) {
      toast.error('Please log in to add tags');
      return;
    }
    
    if (isSharedCloset) {
      toast.error('Cannot modify outfits in a shared closet');
      return;
    }

    try {
      const { error } = await supabase
        .from('outfit_tags')
        .insert({
          outfit_id: outfitId,
          tag_name: tagName
        });

      if (error) throw error;
      toast.success('Tag added successfully');
    } catch (error: any) {
      console.error('Error adding outfit tag:', error);
      if (error.code === '23505') {
        toast.error('This tag already exists for this outfit');
      } else {
        toast.error('Failed to add tag');
      }
    }
  }, [userId, isSharedCloset]);

  const removeOutfitTag = useCallback(async (tagId: string): Promise<void> => {
    if (!userId) {
      toast.error('Please log in to remove tags');
      return;
    }
    
    if (isSharedCloset) {
      toast.error('Cannot modify outfits in a shared closet');
      return;
    }

    try {
      const { error } = await supabase
        .from('outfit_tags')
        .delete()
        .eq('id', tagId);

      if (error) throw error;
      toast.success('Tag removed successfully');
    } catch (error: any) {
      console.error('Error removing outfit tag:', error);
      toast.error('Failed to remove tag');
    }
  }, [userId, isSharedCloset]);

  const getRelatedItems = useCallback(async (outfitId: string): Promise<ClothingItem[]> => {
    try {
      const { data, error } = await supabase
        .from('outfit_related_items')
        .select(`
          clothing_items:clothing_item_id (*)
        `)
        .eq('outfit_id', outfitId);

      if (error) throw error;

      return data?.map(item => {
        const clothingItem = item.clothing_items;
        return {
          id: clothingItem.id,
          name: clothingItem.name || "",
          brand: clothingItem.brand || "",
          color: clothingItem.color || "",
          category: clothingItem.category || "",
          subcategories: clothingItem.subcategories || [],
          status: clothingItem.status as any || "PURCHASED",
          storeUrl: clothingItem.store_url || "",
          images: clothingItem.image_url ? [clothingItem.image_url] : [],
          user_id: clothingItem.user_id,
          description: clothingItem.description || "",
          favorite: clothingItem.favorite || false,
          lifestyle_tags: clothingItem.lifestyle_tags || []
        };
      }) || [];
    } catch (error) {
      console.error('Error fetching related items:', error);
      return [];
    }
  }, []);

  const addRelatedItem = useCallback(async (outfitId: string, clothingItemId: string): Promise<void> => {
    if (!userId) {
      toast.error('Please log in to update outfits');
      return;
    }
    
    if (isSharedCloset) {
      toast.error('Cannot modify outfits in a shared closet');
      return;
    }

    try {
      const { error } = await supabase
        .from('outfit_related_items')
        .insert({
          outfit_id: outfitId,
          clothing_item_id: clothingItemId
        });

      if (error) {
        if (error.code === '23505') {
          toast.error('Item is already added as a related item');
        } else {
          throw error;
        }
        return;
      }
      
      toast.success('Related item added');
    } catch (error: any) {
      console.error('Error adding related item:', error);
      toast.error('Failed to add related item');
    }
  }, [userId, isSharedCloset]);

  const removeRelatedItem = useCallback(async (outfitId: string, clothingItemId: string): Promise<void> => {
    if (!userId) {
      toast.error('Please log in to update outfits');
      return;
    }
    
    if (isSharedCloset) {
      toast.error('Cannot modify outfits in a shared closet');
      return;
    }

    try {
      const { error } = await supabase
        .from('outfit_related_items')
        .delete()
        .eq('outfit_id', outfitId)
        .eq('clothing_item_id', clothingItemId);

      if (error) throw error;
      
      toast.success('Related item removed');
    } catch (error: any) {
      console.error('Error removing related item:', error);
      toast.error('Failed to remove related item');
    }
  }, [userId, isSharedCloset]);

  const addOutfitInspiration = useCallback(async (inspiredOutfitId: string, sourceOutfitId: string): Promise<void> => {
    if (!userId) {
      toast.error('Please log in to tag outfits');
      return;
    }
    
    if (isSharedCloset) {
      toast.error('Cannot modify outfits in a shared closet');
      return;
    }

    try {
      const { error } = await supabase
        .from('outfit_inspirations')
        .insert({
          inspired_outfit_id: inspiredOutfitId,
          source_outfit_id: sourceOutfitId
        });

      if (error) {
        if (error.code === '23505') {
          toast.error('This inspiration link already exists');
        } else {
          throw error;
        }
        return;
      }
      
      toast.success('Outfit tagged as inspired by this look');
    } catch (error: any) {
      console.error('Error adding outfit inspiration:', error);
      toast.error('Failed to tag outfit');
    }
  }, [userId, isSharedCloset]);

  const getInspiredOutfits = useCallback(async (sourceOutfitId: string): Promise<Outfit[]> => {
    try {
      const { data, error } = await supabase
        .from('outfit_inspirations')
        .select(`
          inspired_outfit_id
        `)
        .eq('source_outfit_id', sourceOutfitId);

      if (error) throw error;

      if (!data || data.length === 0) return [];

      // Get the outfit details for each inspired outfit
      const inspiredOutfitIds = data.map(item => item.inspired_outfit_id);
      
      const { data: outfitData, error: outfitError } = await supabase
        .from('outfits')
        .select('*')
        .in('id', inspiredOutfitIds);

      if (outfitError) throw outfitError;

      return outfitData || [];
    } catch (error) {
      console.error('Error fetching inspired outfits:', error);
      return [];
    }
  }, []);

  // Duplicate an outfit as a variation linked to the original
  const duplicateOutfitAsVariation = useCallback(async (outfitId: string, newName?: string): Promise<Outfit | null> => {
    try {
      // Fetch the source outfit
      const { data: source, error: sourceError } = await supabase
        .from('outfits')
        .select('*')
        .eq('id', outfitId)
        .single();

      if (sourceError || !source) throw sourceError || new Error('Outfit not found');

      // Determine the parent: if source is already a variation, link to the same parent
      const parentId = source.parent_outfit_id || source.id;

      // Create the variation
      const { data: variation, error: createError } = await supabase
        .from('outfits')
        .insert({
          name: newName || `${source.name} (V1)`,
          description: source.description,
          user_id: source.user_id,
          folder_id: source.folder_id,
          season: source.season,
          created_by_stylist: source.created_by_stylist,
          stylist_name: source.stylist_name,
          styled_by: source.styled_by,
          style_guide_category: source.style_guide_category,
          parent_outfit_id: parentId,
          customer_visible: false,
          archived: false,
        })
        .select()
        .single();

      if (createError || !variation) throw createError;

      // Copy outfit_items
      const { data: items } = await supabase
        .from('outfit_items')
        .select('clothing_item_id, position, stylist_notes')
        .eq('outfit_id', outfitId)
        .order('position');

      if (items && items.length > 0) {
        const { error: itemsError } = await supabase
          .from('outfit_items')
          .insert(items.map(i => ({ ...i, outfit_id: variation.id })));
        if (itemsError) throw itemsError;
      }

      // Copy outfit_product_items
      const { data: products } = await supabase
        .from('outfit_product_items')
        .select('product_id, position, stylist_notes')
        .eq('outfit_id', outfitId)
        .order('position');

      if (products && products.length > 0) {
        const { error: productsError } = await supabase
          .from('outfit_product_items')
          .insert(products.map(p => ({ ...p, outfit_id: variation.id })));
        if (productsError) throw productsError;
      }

      // Copy tags
      const { data: tags } = await supabase
        .from('outfit_tags')
        .select('tag_name')
        .eq('outfit_id', outfitId);

      if (tags && tags.length > 0) {
        await supabase
          .from('outfit_tags')
          .insert(tags.map(t => ({ outfit_id: variation.id, tag_name: t.tag_name })));
      }

      setOutfits(prev => [...prev, variation]);
      toast.success('Outfit variation created');
      return variation;
    } catch (error) {
      console.error('Error duplicating outfit as variation:', error);
      toast.error('Failed to create variation');
      return null;
    }
  }, []);

  // Get all variations of an outfit (siblings + parent)
  const getOutfitVariations = useCallback(async (outfitId: string): Promise<Outfit[]> => {
    try {
      // First check if this outfit has a parent or is a parent
      const { data: outfit } = await withTimeout(
        supabase
          .from('outfits')
          .select('id, parent_outfit_id')
          .eq('id', outfitId)
          .single(),
        DEFAULT_QUERY_TIMEOUT_MS,
        'load outfit family'
      );

      if (!outfit) return [];

      const parentId = outfit.parent_outfit_id || outfit.id;

      // Fetch all outfits in this family (parent + all variations)
      const { data: variations, error } = await withTimeout(
        supabase
          .from('outfits')
          .select('*')
          .or(`id.eq.${parentId},parent_outfit_id.eq.${parentId}`)
          .eq('archived', false)
          .order('created_at'),
        DEFAULT_QUERY_TIMEOUT_MS,
        'load outfit variations'
      );

      if (error) throw error;
      return variations || [];
    } catch (error) {
      console.error('Error fetching outfit variations:', error);
      throw error;
    }
  }, []);

  const refreshOutfits = useCallback(async () => {
    if (!userId) {
      setFolders([]);
      setOutfits([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      // Fetch folders
      const { data: folderData, error: folderError } = await supabase
        .from('outfit_folders')
        .select('*')
        .eq('user_id', userId);

      if (folderError) throw folderError;
      
      // Fetch outfits (exclude archived ones for Style Magazine)
      let outfitQuery = supabase
        .from('outfits')
        .select('*')
        .eq('user_id', userId)
        .eq('archived', false);

      // Customers only see outfits marked as visible
      if (!isPrivilegedUser) {
        outfitQuery = outfitQuery.eq('customer_visible', true);
      }

      // Refresh the outfit list and re-prime the items/tags maps in parallel.
      const [outfitResult, bundleResult] = await Promise.all([
        outfitQuery,
        fetchOutfitBundles(userId).catch((err) => {
          console.error('[OutfitContext] get_outfit_bundles refresh failed; cards will fall back to per-card fetch', err);
          return null;
        }),
      ]);

      const { data: outfitData, error: outfitError } = outfitResult;
      if (outfitError) throw outfitError;

      setFolders(folderData);
      setOutfits(outfitData);
      setOutfitItemsById(bundleResult?.itemsMap ?? {});
      setOutfitTagsById(bundleResult?.tagsMap ?? {});
    } catch (error: any) {
      console.error('Error fetching outfit data:', error);
      toast.error('Failed to load outfits');
    } finally {
      setIsLoading(false);
    }
  }, [userId, isPrivilegedUser, fetchOutfitBundles]);

  // Primary outfits = parents + standalone (no parent_outfit_id), with variation counts
  const primaryOutfits: OutfitWithVariations[] = useMemo(() => {
    const parents = outfits.filter((o) => !o.parent_outfit_id);
    const variationCounts: Record<string, number> = {};
    outfits.forEach((o) => {
      if (o.parent_outfit_id) {
        variationCounts[o.parent_outfit_id] = (variationCounts[o.parent_outfit_id] || 0) + 1;
      }
    });
    return parents.map((o) => ({ ...o, variation_count: variationCounts[o.id] || 0 }));
  }, [outfits]);

  const value = useMemo(() => ({
    folders,
    outfits,
    primaryOutfits,
    isLoading,
    createFolder,
    updateFolder,
    deleteFolder,
    createOutfit,
    updateOutfit,
    deleteOutfit,
    addItemToOutfit,
    removeItemFromOutfit,
    reorderOutfitItems,
    getOutfitsByFolder,
    getFolderById,
    getOutfitById,
    getOutfitItems,
    isSharedCloset,
    addProductToOutfit,
    removeProductFromOutfit,
    getOutfitTags,
    addOutfitTag,
    removeOutfitTag,
    getRelatedItems,
    addRelatedItem,
    removeRelatedItem,
    addOutfitInspiration,
    getInspiredOutfits,
    duplicateOutfitAsVariation,
    getOutfitVariations,
    refreshOutfits
  }), [
    folders, outfits, primaryOutfits, isLoading,
    createFolder, updateFolder, deleteFolder,
    createOutfit, updateOutfit, deleteOutfit,
    addItemToOutfit, removeItemFromOutfit, reorderOutfitItems,
    getOutfitsByFolder, getFolderById, getOutfitById, getOutfitItems,
    isSharedCloset,
    addProductToOutfit, removeProductFromOutfit,
    getOutfitTags, addOutfitTag, removeOutfitTag,
    getRelatedItems, addRelatedItem, removeRelatedItem,
    addOutfitInspiration, getInspiredOutfits,
    duplicateOutfitAsVariation, getOutfitVariations, refreshOutfits
  ]);

  return (
    <OutfitContext.Provider value={value}>
      {children}
    </OutfitContext.Provider>
  );
};

export const useOutfit = () => {
  const context = useContext(OutfitContext);
  if (context === undefined) {
    throw new Error("useOutfit must be used within an OutfitProvider");
  }
  return context;
};
